import { useEffect, useMemo, useState } from "react";

const CERTIFICATE_ID_PATTERN = /^[A-Z]{2}-\d{3}-\d{4}$/;
const CERTIFICATE_BACKGROUND = {
  top: "#f8f4ea",
  bottom: "#efe6d4",
  accent: "#b84c2a",
  ink: "#181613",
  soft: "#6c675d",
  rule: "#d9cfbe",
  gold: "#b8995b",
};

const WORKSHOPS = [
  {
    slug: "mit-pune-vibe-coding",
    qrCode: "MIT-VC-2026",
    title: "Generative AI & Vibe Coding Workshop",
    subtitle: "Hands-on workshop for students building their first AI-assisted products.",
    college: "MIT Institute of Design, Pune",
    date: "March 26, 2026",
    issueDate: "March 26, 2026",
    venue: "Pune, Maharashtra",
    host: "Kuldeep Anand",
    hostTitle: "Founder & CEO, Visomni",
    certificatePrefix: "KA",
    templateLabel: "Canva certificate background",
    attendees: [
      { name: "Riya Sharma", phone: "9876543210", certificateId: "KA-341-2026" },
      { name: "Aditya Rao", phone: "9123456789", certificateId: "KA-342-2026" },
      { name: "Neha Verma", phone: "9000000001", certificateId: "KA-343-2026" },
      { name: "Sarthak Jain", phone: "9000000002", certificateId: "KA-344-2026" },
    ],
  },
  {
    slug: "srm-chennai-gen-ai",
    qrCode: "SRM-GA-2026",
    title: "Generative AI Product Sprint",
    subtitle: "A workshop on prototyping, prompting, and shipping student ideas faster.",
    college: "SRM Institute of Science and Technology, Chennai",
    date: "February 14, 2026",
    issueDate: "February 14, 2026",
    venue: "Chennai, Tamil Nadu",
    host: "Kuldeep Anand",
    hostTitle: "Founder & CEO, Visomni",
    certificatePrefix: "KB",
    templateLabel: "Canva certificate background",
    attendees: [
      { name: "Harini S", phone: "9988776655", certificateId: "KB-118-2026" },
      { name: "Varun Krishna", phone: "9012345678", certificateId: "KB-119-2026" },
    ],
  },
];

const CERTIFICATE_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --bg: #f7f6f2;
    --bg-soft: #efebe2;
    --paper: #fffdf8;
    --ink: #0e0e0b;
    --ink-soft: #5b554d;
    --ink-faint: #9d978e;
    --accent: #b84c2a;
    --rule: #dfd8cb;
    --serif: 'Cormorant Garamond', Georgia, serif;
    --sans: 'DM Sans', system-ui, sans-serif;
  }

  .cert-shell, .cert-shell * { box-sizing: border-box; }
  .cert-shell {
    min-height: 100vh;
    background:
      radial-gradient(circle at top left, rgba(184, 76, 42, 0.08), transparent 28%),
      radial-gradient(circle at bottom right, rgba(14, 14, 11, 0.06), transparent 24%),
      var(--bg);
    color: var(--ink);
    font-family: var(--sans);
  }

  .cert-shell a {
    color: inherit;
    text-decoration: none;
  }

  .cert-topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    backdrop-filter: blur(12px);
    background: rgba(247, 246, 242, 0.9);
    border-bottom: 1px solid var(--rule);
  }

  .cert-topbar-inner {
    max-width: 1120px;
    margin: 0 auto;
    padding: 18px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .cert-brand {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .cert-brand-mark {
    width: 38px;
    height: 38px;
    border-radius: 999px;
    border: 1px solid var(--rule);
    display: grid;
    place-items: center;
    font-family: var(--serif);
    font-size: 18px;
    background: rgba(255, 255, 255, 0.8);
  }

  .cert-brand-title {
    font-family: var(--serif);
    font-size: 22px;
    font-weight: 500;
  }

  .cert-brand-subtitle {
    color: var(--ink-soft);
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .cert-link-row {
    display: flex;
    gap: 24px;
    align-items: center;
    flex-wrap: wrap;
  }

  .cert-link-row a {
    color: var(--ink-soft);
    border-bottom: 1px solid transparent;
    font-size: 13px;
  }

  .cert-link-row a:hover {
    color: var(--accent);
    border-bottom-color: var(--accent);
  }

  .cert-container {
    max-width: 1120px;
    margin: 0 auto;
    padding: 40px 32px 80px;
  }

  .cert-hero {
    padding: 42px 0 24px;
    display: grid;
    gap: 18px;
  }

  .cert-eyebrow {
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--accent);
    font-weight: 600;
  }

  .cert-hero h1 {
    font-family: var(--serif);
    font-size: clamp(44px, 7vw, 74px);
    line-height: 0.98;
    font-weight: 500;
    max-width: 720px;
  }

  .cert-hero p {
    color: var(--ink-soft);
    font-size: 17px;
    line-height: 1.75;
    max-width: 700px;
  }

  .cert-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
    gap: 32px;
    align-items: start;
  }

  .cert-card, .cert-panel {
    background: rgba(255, 255, 255, 0.72);
    border: 1px solid var(--rule);
    border-radius: 24px;
    box-shadow: 0 20px 50px rgba(14, 14, 11, 0.05);
  }

  .cert-card {
    padding: 32px;
  }

  .cert-panel {
    padding: 26px;
    position: sticky;
    top: 96px;
  }

  .cert-section-label {
    color: var(--ink-faint);
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-size: 10px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  .cert-form-grid {
    display: grid;
    gap: 18px;
  }

  .cert-label {
    display: grid;
    gap: 8px;
    font-size: 13px;
    color: var(--ink-soft);
  }

  .cert-input, .cert-select {
    width: 100%;
    border: 1px solid var(--rule);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.95);
    color: var(--ink);
    padding: 16px 18px;
    font-size: 16px;
    font-family: var(--sans);
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .cert-input:focus, .cert-select:focus {
    border-color: rgba(184, 76, 42, 0.75);
    box-shadow: 0 0 0 4px rgba(184, 76, 42, 0.08);
  }

  .cert-help, .cert-meta, .cert-empty {
    color: var(--ink-soft);
    font-size: 14px;
    line-height: 1.7;
  }

  .cert-help strong, .cert-meta strong {
    color: var(--ink);
  }

  .cert-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .cert-button, .cert-secondary {
    appearance: none;
    border-radius: 999px;
    padding: 13px 22px;
    font-size: 13px;
    letter-spacing: 0.04em;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.15s, background 0.2s, border-color 0.2s, color 0.2s;
  }

  .cert-button {
    border: none;
    background: var(--ink);
    color: var(--bg);
  }

  .cert-button:hover {
    transform: translateY(-1px);
    background: var(--accent);
  }

  .cert-secondary {
    background: transparent;
    border: 1px solid var(--rule);
    color: var(--ink-soft);
  }

  .cert-secondary:hover {
    color: var(--ink);
    border-color: var(--accent);
  }

  .cert-status {
    margin-top: 18px;
    padding: 14px 16px;
    border-radius: 16px;
    font-size: 14px;
    line-height: 1.6;
  }

  .cert-status.error {
    background: rgba(184, 76, 42, 0.08);
    border: 1px solid rgba(184, 76, 42, 0.2);
    color: #8d341b;
  }

  .cert-status.success {
    background: rgba(42, 104, 63, 0.08);
    border: 1px solid rgba(42, 104, 63, 0.2);
    color: #2a683f;
  }

  .cert-workshop-list {
    display: grid;
    gap: 12px;
    margin-top: 20px;
  }

  .cert-workshop-button {
    text-align: left;
    border: 1px solid var(--rule);
    background: rgba(255, 255, 255, 0.8);
    border-radius: 20px;
    padding: 18px 20px;
    cursor: pointer;
    transition: border-color 0.2s, transform 0.15s;
  }

  .cert-workshop-button:hover, .cert-workshop-button.active {
    border-color: rgba(184, 76, 42, 0.45);
    transform: translateY(-1px);
  }

  .cert-workshop-title {
    font-family: var(--serif);
    font-size: 26px;
    margin-bottom: 4px;
  }

  .cert-workshop-meta {
    color: var(--ink-soft);
    font-size: 14px;
  }

  .cert-preview-frame {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.9), rgba(250, 247, 240, 0.9));
    border: 1px solid var(--rule);
    border-radius: 28px;
    padding: 18px;
  }

  .cert-canvas-note {
    margin-top: 12px;
    color: var(--ink-faint);
    font-size: 12px;
    line-height: 1.6;
  }

  .cert-fact-list {
    display: grid;
    gap: 16px;
    margin-top: 20px;
  }

  .cert-fact-row {
    display: grid;
    gap: 4px;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(223, 216, 203, 0.8);
  }

  .cert-fact-row:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }

  .cert-fact-label {
    color: var(--ink-faint);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 10px;
    font-weight: 600;
  }

  .cert-fact-value {
    color: var(--ink);
    font-size: 15px;
    line-height: 1.6;
  }

  .cert-verify-badge {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 999px;
    background: rgba(42, 104, 63, 0.1);
    color: #2a683f;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .cert-not-found {
    padding: 56px 32px;
    text-align: center;
  }

  .cert-not-found h2 {
    font-family: var(--serif);
    font-size: 42px;
    font-weight: 500;
    margin-bottom: 10px;
  }

  .cert-not-found p {
    color: var(--ink-soft);
    font-size: 16px;
    line-height: 1.8;
    max-width: 520px;
    margin: 0 auto 24px;
  }

  @media (max-width: 900px) {
    .cert-topbar-inner,
    .cert-container {
      padding-left: 22px;
      padding-right: 22px;
    }

    .cert-grid {
      grid-template-columns: 1fr;
    }

    .cert-panel {
      position: static;
    }
  }

  @media (max-width: 640px) {
    .cert-card,
    .cert-panel {
      border-radius: 20px;
      padding: 22px;
    }

    .cert-topbar-inner {
      padding-top: 14px;
      padding-bottom: 14px;
    }

    .cert-link-row {
      gap: 16px;
    }

    .cert-workshop-title {
      font-size: 22px;
    }
  }
`;

function navigateTo(pathname, search = "") {
  window.history.pushState({}, "", `${pathname}${search}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function buildCertificateUrl(certificateId) {
  return `${window.location.origin}/certificate/${certificateId}`;
}

function normalizePhone(value) {
  return value.replace(/\D/g, "").slice(-10);
}

function buildIndexes() {
  const workshopsBySlug = new Map();
  const certificatesById = new Map();

  WORKSHOPS.forEach((workshop) => {
    workshopsBySlug.set(workshop.slug, workshop);
    workshop.attendees.forEach((attendee) => {
      certificatesById.set(attendee.certificateId, {
        ...attendee,
        workshop,
      });
    });
  });

  return { workshopsBySlug, certificatesById };
}

function getRouteState() {
  const pathname = window.location.pathname;
  const search = new URLSearchParams(window.location.search);
  const candidateId = pathname.replace(/^\/certificate\//, "").replace(/\/$/, "");

  if (CERTIFICATE_ID_PATTERN.test(candidateId)) {
    return {
      mode: "certificate",
      certificateId: candidateId,
      claimed: search.get("claimed") === "1",
    };
  }

  return {
    mode: "claim",
    workshopSlug: search.get("workshop") || "",
  };
}

function findWorkshopFromRoute(workshopsBySlug, workshopSlug) {
  if (workshopSlug && workshopsBySlug.has(workshopSlug)) {
    return workshopsBySlug.get(workshopSlug);
  }

  return WORKSHOPS[0];
}

function drawCertificate(canvas, record) {
  const context = canvas.getContext("2d");
  canvas.width = 1600;
  canvas.height = 1132;

  const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, CERTIFICATE_BACKGROUND.top);
  gradient.addColorStop(1, CERTIFICATE_BACKGROUND.bottom);
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.strokeStyle = CERTIFICATE_BACKGROUND.rule;
  context.lineWidth = 8;
  context.strokeRect(36, 36, canvas.width - 72, canvas.height - 72);

  context.strokeStyle = CERTIFICATE_BACKGROUND.gold;
  context.lineWidth = 2;
  context.strokeRect(72, 72, canvas.width - 144, canvas.height - 144);

  context.fillStyle = CERTIFICATE_BACKGROUND.soft;
  context.font = "500 28px Georgia";
  context.textAlign = "center";
  context.fillText(record.workshop.college, canvas.width / 2, 178);

  context.fillStyle = CERTIFICATE_BACKGROUND.accent;
  context.font = "600 26px Arial";
  context.fillText("CERTIFICATE OF PARTICIPATION", canvas.width / 2, 250);

  context.fillStyle = CERTIFICATE_BACKGROUND.ink;
  context.font = "500 78px Georgia";
  context.fillText(record.name, canvas.width / 2, 432);

  context.strokeStyle = CERTIFICATE_BACKGROUND.gold;
  context.lineWidth = 1.5;
  context.beginPath();
  context.moveTo(320, 462);
  context.lineTo(1280, 462);
  context.stroke();

  context.fillStyle = CERTIFICATE_BACKGROUND.soft;
  context.font = "500 28px Arial";
  context.fillText("This certifies that the participant successfully completed", canvas.width / 2, 548);

  context.fillStyle = CERTIFICATE_BACKGROUND.ink;
  context.font = "500 48px Georgia";
  context.fillText(record.workshop.title, canvas.width / 2, 630);

  context.fillStyle = CERTIFICATE_BACKGROUND.soft;
  context.font = "500 26px Arial";
  context.fillText(`Issued on ${record.workshop.issueDate} · ${record.workshop.venue}`, canvas.width / 2, 692);

  context.font = "500 24px Arial";
  context.fillText(`Certificate ID ${record.certificateId}`, canvas.width / 2, 758);

  context.font = "500 28px Georgia";
  context.textAlign = "left";
  context.fillStyle = CERTIFICATE_BACKGROUND.ink;
  context.fillText(record.workshop.host, 260, 934);
  context.textAlign = "right";
  context.fillText(record.workshop.host, 1340, 934);

  context.strokeStyle = CERTIFICATE_BACKGROUND.gold;
  context.beginPath();
  context.moveTo(160, 952);
  context.lineTo(520, 952);
  context.moveTo(1080, 952);
  context.lineTo(1440, 952);
  context.stroke();

  context.fillStyle = CERTIFICATE_BACKGROUND.soft;
  context.font = "500 20px Arial";
  context.textAlign = "left";
  context.fillText(record.workshop.hostTitle.toUpperCase(), 160, 986);
  context.textAlign = "right";
  context.fillText("DIGITAL VERIFICATION", 1440, 986);

  context.textAlign = "center";
  context.font = "500 20px Arial";
  context.fillText(buildCertificateUrl(record.certificateId), canvas.width / 2, 1040);
}

function CertificateArtwork({ record }) {
  const displayRecord = record || {
    name: "Student Name",
    certificateId: "KA-000-0000",
    workshop: WORKSHOPS[0],
  };

  return (
    <div
      style={{
        background: `linear-gradient(180deg, ${CERTIFICATE_BACKGROUND.top}, ${CERTIFICATE_BACKGROUND.bottom})`,
        border: `1px solid ${CERTIFICATE_BACKGROUND.rule}`,
        borderRadius: "24px",
        padding: "18px",
      }}
    >
      <div
        style={{
          background: "rgba(255,255,255,0.5)",
          border: `2px solid ${CERTIFICATE_BACKGROUND.gold}`,
          borderRadius: "18px",
          padding: "34px 26px",
          minHeight: "520px",
          color: CERTIFICATE_BACKGROUND.ink,
          display: "grid",
          gap: "16px",
          textAlign: "center",
        }}
      >
        <div style={{ color: CERTIFICATE_BACKGROUND.accent, letterSpacing: "0.18em", fontSize: "12px", fontWeight: 600 }}>
          CERTIFICATE OF PARTICIPATION
        </div>
        <div style={{ fontFamily: "var(--serif)", fontSize: "20px", color: CERTIFICATE_BACKGROUND.soft }}>
          {displayRecord.workshop.college}
        </div>
        <div style={{ fontSize: "13px", color: CERTIFICATE_BACKGROUND.soft, letterSpacing: "0.12em", textTransform: "uppercase" }}>
          Issued to
        </div>
        <div style={{ fontFamily: "var(--serif)", fontSize: "clamp(40px, 5vw, 56px)", lineHeight: 1 }}>
          {displayRecord.name}
        </div>
        <div style={{ width: "70%", margin: "0 auto", borderBottom: `1px solid ${CERTIFICATE_BACKGROUND.gold}` }} />
        <div style={{ fontSize: "16px", lineHeight: 1.8, color: CERTIFICATE_BACKGROUND.soft, maxWidth: "540px", margin: "0 auto" }}>
          Successfully completed the <strong style={{ color: CERTIFICATE_BACKGROUND.ink }}>{displayRecord.workshop.title}</strong> workshop conducted by Kuldeep Anand.
        </div>
        <div style={{ fontSize: "15px", color: CERTIFICATE_BACKGROUND.soft }}>
          {displayRecord.workshop.issueDate} · {displayRecord.workshop.venue}
        </div>
        <div style={{ marginTop: "auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", alignItems: "end" }}>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontFamily: "var(--serif)", fontSize: "28px" }}>{displayRecord.workshop.host}</div>
            <div style={{ borderBottom: `1px solid ${CERTIFICATE_BACKGROUND.gold}`, marginTop: "4px" }} />
            <div style={{ marginTop: "8px", fontSize: "12px", letterSpacing: "0.1em", color: CERTIFICATE_BACKGROUND.soft }}>
              {displayRecord.workshop.hostTitle.toUpperCase()}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "12px", color: CERTIFICATE_BACKGROUND.soft, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Verified at
            </div>
            <div style={{ fontFamily: "monospace", fontSize: "13px", marginTop: "8px" }}>
              /certificate/{displayRecord.certificateId}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClaimPage({ selectedWorkshop, onSelectWorkshop, onSubmit, error, success }) {
  const [phone, setPhone] = useState("");

  useEffect(() => {
    setPhone("");
  }, [selectedWorkshop?.slug]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(phone);
  };

  return (
    <div className="cert-container">
      <section className="cert-hero">
        <div className="cert-eyebrow">Workshop Certificates</div>
        <h1>Claim your workshop certificate with a verifiable public link.</h1>
        <p>
          Each workshop QR code opens a prefilled claim page. Students enter the same phone number used during registration, and their issued certificate resolves to a permanent verification URL at <strong>/certificate/XX-XXX-XXXX</strong>.
        </p>
      </section>

      <section className="cert-grid">
        <div className="cert-card">
          <div className="cert-section-label">Claim Certificate</div>
          <form className="cert-form-grid" onSubmit={handleSubmit}>
            <label className="cert-label">
              Workshop
              <select
                className="cert-select"
                value={selectedWorkshop.slug}
                onChange={(event) => onSelectWorkshop(event.target.value)}
              >
                {WORKSHOPS.map((workshop) => (
                  <option key={workshop.slug} value={workshop.slug}>
                    {workshop.title} · {workshop.college}
                  </option>
                ))}
              </select>
            </label>

            <div className="cert-help">
              <strong>{selectedWorkshop.title}</strong><br />
              {selectedWorkshop.college} · {selectedWorkshop.date}
            </div>

            <label className="cert-label">
              Registered phone number
              <input
                className="cert-input"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="Enter your 10-digit phone number"
                value={phone}
                onChange={(event) => setPhone(normalizePhone(event.target.value))}
              />
            </label>

            <div className="cert-actions">
              <button type="submit" className="cert-button">Claim Certificate</button>
              <button
                type="button"
                className="cert-secondary"
                onClick={() => navigateTo(`/certificate?workshop=${selectedWorkshop.slug}`)}
              >
                Copy QR-ready URL
              </button>
            </div>
          </form>

          {error ? <div className="cert-status error">{error}</div> : null}
          {success ? <div className="cert-status success">{success}</div> : null}

          <div className="cert-workshop-list">
            {WORKSHOPS.map((workshop) => (
              <button
                key={workshop.slug}
                type="button"
                className={`cert-workshop-button${selectedWorkshop.slug === workshop.slug ? " active" : ""}`}
                onClick={() => onSelectWorkshop(workshop.slug)}
              >
                <div className="cert-workshop-title">{workshop.title}</div>
                <div className="cert-workshop-meta">
                  {workshop.college} · QR code <strong>{workshop.qrCode}</strong>
                </div>
              </button>
            ))}
          </div>
        </div>

        <aside className="cert-panel">
          <div className="cert-section-label">Preview</div>
          <div className="cert-preview-frame">
            <CertificateArtwork />
          </div>
          <div className="cert-canvas-note">
            The current build uses sample workshop and attendee records in-code so the flow is testable now. The production version can replace this list with Google Sheets data and a Canva-exported certificate background without changing the URL structure.
          </div>
          <div className="cert-fact-list">
            <div className="cert-fact-row">
              <div className="cert-fact-label">Certificate route</div>
              <div className="cert-fact-value">`/certificate/KA-341-2026` style links for public verification.</div>
            </div>
            <div className="cert-fact-row">
              <div className="cert-fact-label">QR behavior</div>
              <div className="cert-fact-value">Each workshop QR can point to `/certificate?workshop={selectedWorkshop.slug}`.</div>
            </div>
            <div className="cert-fact-row">
              <div className="cert-fact-label">Template source</div>
              <div className="cert-fact-value">{selectedWorkshop.templateLabel} with student name overlaid at generation time.</div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}

function CertificatePage({ record, claimed }) {
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (!claimed) {
      return undefined;
    }

    setFeedback("Certificate claimed successfully. Your public verification page is live.");
    const timer = window.setTimeout(() => setFeedback(""), 2600);
    return () => window.clearTimeout(timer);
  }, [claimed]);

  const certificateUrl = buildCertificateUrl(record.certificateId);

  const handleDownload = async () => {
    const canvas = document.createElement("canvas");
    drawCertificate(canvas, record);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) {
      setFeedback("Could not generate the certificate PNG. Please try again.");
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `${record.certificateId}.png`;
    link.click();
    URL.revokeObjectURL(objectUrl);
    setFeedback("Certificate PNG downloaded.");
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${record.name} · Workshop Certificate`,
          text: `Verify this workshop certificate for ${record.name}.`,
          url: certificateUrl,
        });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(certificateUrl);
      }

      setFeedback("Verification link ready to share.");
    } catch (error) {
      setFeedback("Sharing was cancelled.");
    }
  };

  const maskedPhone = `******${record.phone.slice(-4)}`;

  return (
    <div className="cert-container">
      <section className="cert-hero">
        <div className="cert-eyebrow">Certificate Verification</div>
        <h1>{record.name}</h1>
        <div className="cert-verify-badge">
          <span>Verified</span>
          <span>{record.certificateId}</span>
        </div>
        <p>
          This public page verifies that the certificate was issued for <strong>{record.workshop.title}</strong> at {record.workshop.college}. The certificate link is permanent and can be shared with recruiters, faculty, and event partners.
        </p>
      </section>

      <section className="cert-grid">
        <div className="cert-card">
          <div className="cert-section-label">Issued Certificate</div>
          <div className="cert-preview-frame">
            <CertificateArtwork record={record} />
          </div>
          {feedback ? <div className="cert-status success">{feedback}</div> : null}
          <div className="cert-actions" style={{ marginTop: "20px" }}>
            <button type="button" className="cert-button" onClick={handleDownload}>Download PNG</button>
            <button type="button" className="cert-secondary" onClick={handleShare}>Share Verification Link</button>
            <button type="button" className="cert-secondary" onClick={() => navigateTo(`/certificate?workshop=${record.workshop.slug}`)}>
              Claim Another Certificate
            </button>
          </div>
        </div>

        <aside className="cert-panel">
          <div className="cert-section-label">Verification Details</div>
          <div className="cert-fact-list">
            <div className="cert-fact-row">
              <div className="cert-fact-label">Issued to</div>
              <div className="cert-fact-value">{record.name}</div>
            </div>
            <div className="cert-fact-row">
              <div className="cert-fact-label">Workshop</div>
              <div className="cert-fact-value">{record.workshop.title}</div>
            </div>
            <div className="cert-fact-row">
              <div className="cert-fact-label">Institution</div>
              <div className="cert-fact-value">{record.workshop.college}</div>
            </div>
            <div className="cert-fact-row">
              <div className="cert-fact-label">Issued on</div>
              <div className="cert-fact-value">{record.workshop.issueDate}</div>
            </div>
            <div className="cert-fact-row">
              <div className="cert-fact-label">Verified route</div>
              <div className="cert-fact-value">{certificateUrl}</div>
            </div>
            <div className="cert-fact-row">
              <div className="cert-fact-label">Matched phone</div>
              <div className="cert-fact-value">{maskedPhone}</div>
            </div>
          </div>
          <div className="cert-canvas-note">
            In production, this page should be served from persisted certificate records after whitelist validation against Google Sheets and certificate rendering from the Canva background asset.
          </div>
        </aside>
      </section>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="cert-container">
      <div className="cert-card cert-not-found">
        <div className="cert-section-label">Certificate Lookup</div>
        <h2>Certificate not found.</h2>
        <p>
          The verification URL does not match an issued certificate in the current dataset. Check the certificate ID or return to the claim page and use the workshop QR code again.
        </p>
        <div className="cert-actions" style={{ justifyContent: "center" }}>
          <button type="button" className="cert-button" onClick={() => navigateTo("/certificate")}>Go to Claim Page</button>
          <a className="cert-secondary" href="/">Back to Home</a>
        </div>
      </div>
    </div>
  );
}

export default function CertApp() {
  const indexes = useMemo(buildIndexes, []);
  const routeState = getRouteState();
  const [selectedWorkshop, setSelectedWorkshop] = useState(
    findWorkshopFromRoute(indexes.workshopsBySlug, routeState.workshopSlug)
  );
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (routeState.mode === "claim") {
      setSelectedWorkshop(findWorkshopFromRoute(indexes.workshopsBySlug, routeState.workshopSlug));
      setError("");
      setSuccess("");
    }
  }, [routeState.mode, routeState.workshopSlug, indexes.workshopsBySlug]);

  const handleWorkshopSelect = (workshopSlug) => {
    setSelectedWorkshop(findWorkshopFromRoute(indexes.workshopsBySlug, workshopSlug));
    navigateTo("/certificate", `?workshop=${workshopSlug}`);
  };

  const handleClaimSubmit = (phone) => {
    const normalizedPhone = normalizePhone(phone);
    if (normalizedPhone.length !== 10) {
      setError("Enter the same 10-digit phone number used during workshop registration.");
      setSuccess("");
      return;
    }

    const attendee = selectedWorkshop.attendees.find((item) => item.phone === normalizedPhone);
    if (!attendee) {
      setError(`We could not find that number in the ${selectedWorkshop.title} whitelist. This is where the Google Sheets lookup will happen in production.`);
      setSuccess("");
      return;
    }

    setError("");
    setSuccess(`Matched ${attendee.name}. Redirecting to the public verification page.`);
    navigateTo(`/certificate/${attendee.certificateId}`, "?claimed=1");
  };

  const record = routeState.mode === "certificate"
    ? indexes.certificatesById.get(routeState.certificateId)
    : null;

  return (
    <div className="cert-shell">
      <style>{CERTIFICATE_STYLE}</style>

      <header className="cert-topbar">
        <div className="cert-topbar-inner">
          <div className="cert-brand">
            <div className="cert-brand-mark">KA</div>
            <div>
              <a className="cert-brand-title" href="/">Kuldeep Anand</a>
              <div className="cert-brand-subtitle">Workshop Certificate Module</div>
            </div>
          </div>
          <nav className="cert-link-row">
            <a href="/">Homepage</a>
            <a href="/certificate">Claim</a>
            <a href="/certificate/KA-341-2026">Sample Certificate</a>
          </nav>
        </div>
      </header>

      {routeState.mode === "claim" ? (
        <ClaimPage
          selectedWorkshop={selectedWorkshop}
          onSelectWorkshop={handleWorkshopSelect}
          onSubmit={handleClaimSubmit}
          error={error}
          success={success}
        />
      ) : null}

      {routeState.mode === "certificate" && record ? (
        <CertificatePage record={record} claimed={routeState.claimed} />
      ) : null}

      {routeState.mode === "certificate" && !record ? <NotFoundPage /> : null}
    </div>
  );
}
